import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useRuntimeCommunity } from '../../../context';
import * as Styled from './VoteActions.styled';

interface PointsAwardVoteProps {
  readonly maxScore: number;
  readonly localVote: number | null;
  readonly disabled: boolean;
  readonly onVote: (direction: number) => void;
}

export const PointsAwardVote = ({ maxScore, localVote, disabled, onVote }: PointsAwardVoteProps) => {
  const { colors } = useRuntimeCommunity();
  const intl = useIntl();
  const buttonColor = colors?.button ? `#${colors.button}` : '#4caf50';
  const [showPopup, setShowPopup] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);

  // Close popup on outside click
  useEffect(() => {
    if (!showPopup) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
        setShowPopup(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPopup]);

  return (
    <Styled.PointsAwardWrap ref={popupRef}>
      {showPopup && (
        <Styled.PointsPopup>
          {Array.from({ length: maxScore }, (_, i) => {
            const pointValue = i + 1;
            return (
              <Styled.PointOption
                key={pointValue}
                $active={localVote === pointValue}
                $buttonColor={buttonColor}
                onClick={() => {
                  onVote(pointValue);
                  setShowPopup(false);
                }}
                aria-label={intl.formatMessage(
                  { id: 'awardPoints', defaultMessage: 'Award {count, plural, one {# point} other {# points}}' },
                  { count: pointValue }
                )}
                aria-pressed={localVote === pointValue ? 'true' : 'false'}
                type="button"
              >
                {pointValue}
              </Styled.PointOption>
            );
          })}
          <Styled.PointsPopupClose
            onClick={() => setShowPopup(false)}
            aria-label={intl.formatMessage({ id: 'closePointsPanel', defaultMessage: 'Close points panel' })}
            type="button"
          >
            {String('✕')}
          </Styled.PointsPopupClose>
        </Styled.PointsPopup>
      )}

      <Styled.PointsAwardTrigger
        $hasVote={localVote !== null && localVote > 0}
        $buttonColor={buttonColor}
        disabled={disabled}
        onClick={() => !disabled && setShowPopup(!showPopup)}
        type="button"
      >
        {localVote !== null && localVote > 0
          ? intl.formatMessage(
              { id: 'yourScore', defaultMessage: 'Your Score {score}/{max}' },
              { score: localVote, max: maxScore }
            )
          : intl.formatMessage({ id: 'awardPointsButton', defaultMessage: 'Award Points' })}
      </Styled.PointsAwardTrigger>
    </Styled.PointsAwardWrap>
  );
};
